import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Typography,
  Slider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Avatar,
  InputAdornment,
} from '@mui/material';
import {
  Close,
  FilterList,
  ClearAll,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface FilterVehiclesDialogProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: VehicleFilters) => void;
  onClearFilters: () => void;
  currentFilters: VehicleFilters;
}

export interface VehicleFilters {
  status: string[];
  type: string[];
  priceRange: [number, number];
  yearRange: [number, number];
  features: string[];
  seats?: number;
  minVerificationScore?: number;
  minQualityScore?: number;
  hasDocuments?: boolean;
  searchText?: string;
}

const vehicleStatuses = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'inactive', label: 'Inactive', color: 'default' },
  { value: 'maintenance', label: 'Under Maintenance', color: 'warning' },
  { value: 'in_review', label: 'Under Review', color: 'info' },
  { value: 'pending', label: 'Pending Approval', color: 'secondary' }
];

const vehicleTypes = [
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'hatchback', label: 'Hatchback' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'economy', label: 'Economy' }
];

const commonFeatures = [
  'Air Conditioning', 'Bluetooth', 'GPS Navigation', 'Backup Camera', 'Heated Seats',
  'Leather Seats', 'Sunroof', 'Cruise Control', 'Keyless Entry', 'USB Charging',
  'Apple CarPlay', 'Android Auto', 'Premium Sound', 'Parking Sensors', 'Lane Assist'
];

const seatOptions = [2, 4, 5, 6, 7, 8, 9, 12, 15];

export default function FilterVehiclesDialog({ 
  open, 
  onClose, 
  onApplyFilters, 
  onClearFilters, 
  currentFilters 
}: FilterVehiclesDialogProps) {
  const theme = useTheme();
  
  const [filters, setFilters] = useState<VehicleFilters>({
    status: [],
    type: [],
    priceRange: [0, 500],
    yearRange: [2000, new Date().getFullYear()],
    features: [],
    seats: undefined,
    minVerificationScore: undefined,
    minQualityScore: undefined,
    hasDocuments: undefined,
    searchText: ''
  });

  // Initialize filters when dialog opens
  useEffect(() => {
    if (open) {
      setFilters(currentFilters);
    }
  }, [open, currentFilters]);

  const handleStatusToggle = (status: string) => {
    setFilters(prev => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status]
    }));
  };

  const handleTypeToggle = (type: string) => {
    setFilters(prev => ({
      ...prev,
      type: prev.type.includes(type)
        ? prev.type.filter(t => t !== type)
        : [...prev.type, type]
    }));
  };

  const handleFeatureToggle = (feature: string) => {
    setFilters(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handlePriceRangeChange = (event: Event, newValue: number | number[]) => {
    setFilters(prev => ({
      ...prev,
      priceRange: newValue as [number, number]
    }));
  };

  const handleYearRangeChange = (event: Event, newValue: number | number[]) => {
    setFilters(prev => ({
      ...prev,
      yearRange: newValue as [number, number]
    }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters: VehicleFilters = {
      status: [],
      type: [],
      priceRange: [0, 500],
      yearRange: [2000, new Date().getFullYear()],
      features: [],
      seats: undefined,
      minVerificationScore: undefined,
      minQualityScore: undefined,
      hasDocuments: undefined,
      searchText: ''
    };
    setFilters(clearedFilters);
    onClearFilters();
    onClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.type.length > 0) count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 500) count++;
    if (filters.yearRange[0] > 2000 || filters.yearRange[1] < new Date().getFullYear()) count++;
    if (filters.features.length > 0) count++;
    if (filters.seats !== undefined) count++;
    if (filters.minVerificationScore !== undefined) count++;
    if (filters.minQualityScore !== undefined) count++;
    if (filters.hasDocuments !== undefined) count++;
    if (filters.searchText && filters.searchText.trim() !== '') count++;
    return count;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <FilterList />
          </Avatar>
          <Typography variant="h6">
            Filter Vehicles
            {getActiveFilterCount() > 0 && (
              <Chip 
                label={`${getActiveFilterCount()} active`} 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }} 
              />
            )}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflow: 'auto' }}>
        <Box sx={{ mt: 2 }}>
          {/* Search Text */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Search vehicles"
                value={filters.searchText || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                placeholder="Search by make, model, license plate, VIN..."
                InputProps={{
                  startAdornment: <InputAdornment position="start">🔍</InputAdornment>,
                }}
                sx={{ mb: 3 }}
              />
            </Grid>
          </Grid>

          {/* Vehicle Status */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Vehicle Status</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {vehicleStatuses.map((status) => (
                <Chip
                  key={status.value}
                  label={status.label}
                  variant={filters.status.includes(status.value) ? 'filled' : 'outlined'}
                  color={filters.status.includes(status.value) ? 'primary' : 'default'}
                  onClick={() => handleStatusToggle(status.value)}
                  clickable
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Vehicle Type */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Vehicle Type</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {vehicleTypes.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  variant={filters.type.includes(type.value) ? 'filled' : 'outlined'}
                  color={filters.type.includes(type.value) ? 'primary' : 'default'}
                  onClick={() => handleTypeToggle(type.value)}
                  clickable
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Price Range */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Daily Rate Range: ${filters.priceRange[0]} - ${filters.priceRange[1]}
              </Typography>
              <Slider
                value={filters.priceRange}
                onChange={handlePriceRangeChange}
                valueLabelDisplay="auto"
                min={0}
                max={500}
                step={10}
                marks={[
                  { value: 0, label: '$0' },
                  { value: 100, label: '$100' },
                  { value: 200, label: '$200' },
                  { value: 300, label: '$300' },
                  { value: 400, label: '$400' },
                  { value: 500, label: '$500+' }
                ]}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Year Range */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Model Year: {filters.yearRange[0]} - {filters.yearRange[1]}
              </Typography>
              <Slider
                value={filters.yearRange}
                onChange={handleYearRangeChange}
                valueLabelDisplay="auto"
                min={2000}
                max={new Date().getFullYear()}
                step={1}
                marks={[
                  { value: 2000, label: '2000' },
                  { value: 2010, label: '2010' },
                  { value: 2020, label: '2020' },
                  { value: new Date().getFullYear(), label: new Date().getFullYear().toString() }
                ]}
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Number of Seats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Number of Seats</InputLabel>
                <Select
                  value={filters.seats || ''}
                  label="Number of Seats"
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    seats: e.target.value === '' ? undefined : Number(e.target.value) 
                  }))}
                >
                  <MenuItem value="">Any</MenuItem>
                  {seatOptions.map(seat => (
                    <MenuItem key={seat} value={seat}>{seat} seats</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Quality Scores */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Verification Score"
                value={filters.minVerificationScore || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  minVerificationScore: e.target.value === '' ? undefined : Number(e.target.value) 
                }))}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Quality Score"
                value={filters.minQualityScore || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  minQualityScore: e.target.value === '' ? undefined : Number(e.target.value) 
                }))}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Features */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Features</Typography>
            <FormGroup>
              <Grid container spacing={1}>
                {commonFeatures.map((feature) => (
                  <Grid item xs={12} sm={6} md={4} key={feature}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filters.features.includes(feature)}
                          onChange={() => handleFeatureToggle(feature)}
                        />
                      }
                      label={feature}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Document Requirements */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Document Requirements</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hasDocuments === true}
                  indeterminate={filters.hasDocuments === undefined}
                  onChange={(e) => setFilters(prev => ({ 
                    ...prev, 
                    hasDocuments: e.target.checked ? true : undefined 
                  }))}
                />
              }
              label="Only vehicles with complete documentation"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClear} 
          startIcon={<ClearAll />}
          color="secondary"
        >
          Clear All
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        <Button onClick={onClose}>
          Cancel
        </Button>
        
        <Button
          variant="contained"
          onClick={handleApply}
          startIcon={<FilterList />}
        >
          Apply Filters ({getActiveFilterCount()})
        </Button>
      </DialogActions>
    </Dialog>
  );
}